import { useCallback, useState, useRef } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Node,
  BackgroundVariant,
  Panel,
  useReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Plus, Save, Trash2, Wand2, FlaskConical, PanelRightClose, PanelRightOpen, PanelLeftClose, PanelLeftOpen, GitBranch } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { WorkflowNode } from './WorkflowNode';
import { WorkflowTestPanel } from './WorkflowTestPanel';
import { WorkflowVersionHistory, useMockWorkflowVersionHistory, type WorkflowVersion } from './WorkflowVersionHistory';
import { AssetPalette, type AssetDropData } from './AssetPalette';
import { Badge } from '@/components/ui/badge';
import type { Workflow, WorkflowNode as WorkflowNodeType, WorkflowEdge } from '@/types';
import { generateId } from '@/lib/utils';

const nodeTypes = {
  custom: WorkflowNode,
};

interface WorkflowEditorProps {
  workflow: Workflow | null;
  onSave: (workflow: Partial<Workflow>) => Promise<unknown>;
  isLoading?: boolean;
  projectId?: string;
}

const defaultNodes: Node[] = [
  {
    id: 'start',
    type: 'custom',
    position: { x: 250, y: 50 },
    data: { label: 'Start', type: 'start', status: 'completed' },
  },
];

const defaultEdges: WorkflowEdge[] = [];

// Inner component that uses ReactFlow hooks
function WorkflowEditorInner({ workflow, onSave, isLoading, projectId }: WorkflowEditorProps) {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { screenToFlowPosition } = useReactFlow();

  const [nodes, setNodes, onNodesChange] = useNodesState(
    workflow?.nodes?.map((n) => ({
      id: n.id,
      type: 'custom',
      position: n.position,
      data: n.data,
    })) || defaultNodes
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState<WorkflowEdge>(
    workflow?.edges || defaultEdges
  );
  const [isSaving, setIsSaving] = useState(false);
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [showAssetPalette, setShowAssetPalette] = useState(true);
  const [workflowVersion, setWorkflowVersion] = useState(workflow?.version || 1);
  const versions = useMockWorkflowVersionHistory();

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds) as WorkflowEdge[]),
    [setEdges]
  );

  const addNode = (type: string) => {
    const newNode: Node = {
      id: generateId(),
      type: 'custom',
      position: { x: Math.random() * 400 + 100, y: Math.random() * 300 + 100 },
      data: {
        label: getNodeLabel(type),
        type,
        status: 'pending',
      },
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const getNodeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      task: 'New Task',
      decision: 'Decision',
      process: 'Process',
      end: 'End',
    };
    return labels[type] || 'Node';
  };

  // Handle drop from AssetPalette
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const dataStr = event.dataTransfer.getData('application/reactflow');
      if (!dataStr) return;

      try {
        const dropData: AssetDropData = JSON.parse(dataStr);

        // Get the position where the node was dropped
        const position = screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });

        const newNode: Node = {
          id: generateId(),
          type: 'custom',
          position,
          data: {
            ...dropData.nodeData,
            // Set support status based on asset properties
            supportStatus: dropData.asset.mcpCompatible ? 'supported' : 'unsupported',
          },
        };

        setNodes((nds) => [...nds, newNode]);
      } catch (error) {
        console.error('Failed to parse drop data:', error);
      }
    },
    [screenToFlowPosition, setNodes]
  );

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({
        nodes: nodes.map((n) => ({
          id: n.id,
          type: n.type || 'custom',
          position: n.position,
          data: n.data as WorkflowNodeType['data'],
        })),
        edges: edges.map((e) => ({
          id: e.id,
          source: e.source,
          target: e.target,
          type: e.type,
          animated: e.animated,
          label: e.label as string | undefined,
        })),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const clearWorkflow = () => {
    setNodes(defaultNodes);
    setEdges(defaultEdges);
  };

  const handleRestoreVersion = (version: WorkflowVersion) => {
    // Restore nodes and edges from the selected version
    setNodes(
      version.nodes.map((n) => ({
        id: n.id,
        type: 'custom',
        position: n.position,
        data: n.data,
      }))
    );
    setEdges(version.edges);
    setWorkflowVersion(version.version);
  };

  const handleNodeStatusChange = (nodeId: string, status: WorkflowNodeType['data']['status']) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, status } }
          : node
      )
    );
  };

  // Convert nodes to WorkflowNode format for test panel
  const workflowNodes: WorkflowNodeType[] = nodes.map((n) => ({
    id: n.id,
    type: n.type || 'custom',
    position: n.position,
    data: n.data as WorkflowNodeType['data'],
  }));

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="mt-2 text-sm text-muted-foreground">Loading workflow...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-full">
      {/* Asset Palette */}
      {showAssetPalette && (
        <AssetPalette
          projectId={projectId}
          filterTypes={['tool', 'agent', 'template', 'rag_module']}
        />
      )}

      {/* Main Editor Area */}
      <div className="flex-1" ref={reactFlowWrapper}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onDragOver={onDragOver}
          onDrop={onDrop}
          nodeTypes={nodeTypes}
          fitView
          className="bg-muted/30"
        >
          <Panel position="top-left" className="flex gap-2">
            <Button
              variant={showAssetPalette ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setShowAssetPalette(!showAssetPalette)}
            >
              {showAssetPalette ? (
                <PanelLeftClose className="mr-1 h-4 w-4" />
              ) : (
                <PanelLeftOpen className="mr-1 h-4 w-4" />
              )}
              에셋
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline">
                  <Plus className="mr-1 h-4 w-4" />
                  노드 추가
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => addNode('task')}>
                  Task Node
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => addNode('decision')}>
                  Decision Node
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => addNode('process')}>
                  Process Node
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => addNode('end')}>
                  End Node
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button variant="outline" size="sm">
              <Wand2 className="mr-1 h-4 w-4" />
              생성
            </Button>
          </Panel>

          <Panel position="top-right" className="flex items-center gap-2">
            <Badge variant="outline" className="flex items-center gap-1">
              <GitBranch className="h-3 w-3" />
              v{workflowVersion}
            </Badge>
            <WorkflowVersionHistory
              versions={versions}
              currentVersion={workflowVersion}
              onRestore={handleRestoreVersion}
            />
            <Button variant="outline" size="sm" onClick={clearWorkflow}>
              <Trash2 className="mr-1 h-4 w-4" />
              Clear
            </Button>
            <Button
              variant={showTestPanel ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setShowTestPanel(!showTestPanel)}
            >
              <FlaskConical className="mr-1 h-4 w-4" />
              Test
              {showTestPanel ? (
                <PanelRightClose className="ml-1 h-4 w-4" />
              ) : (
                <PanelRightOpen className="ml-1 h-4 w-4" />
              )}
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isSaving}>
              <Save className="mr-1 h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </Panel>

          <Controls />
          <MiniMap
            nodeStrokeWidth={3}
            className="!bg-background"
          />
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
        </ReactFlow>
      </div>

      {/* Test Panel */}
      {showTestPanel && (
        <WorkflowTestPanel
          nodes={workflowNodes}
          onNodeStatusChange={handleNodeStatusChange}
          className="w-96"
        />
      )}
    </div>
  );
}

// Main export component wrapped with ReactFlowProvider
export function WorkflowEditor(props: WorkflowEditorProps) {
  return (
    <ReactFlowProvider>
      <WorkflowEditorInner {...props} />
    </ReactFlowProvider>
  );
}
